import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GetTemplateByID } from "./templates";

import { useLocation } from "react-router-dom";
import useRequest from "../../../api/useRequest";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { CarouselsRequestFactory } from "../../../api/requestFactory";
import { NotificationContext } from "../../../context/notification-context";
import { useNavigate } from "react-router-dom";
import Nav from "./nav";
import DateInUserTimezone from "../../../components/date-in-user-timezone";
import { getAvailableFonts } from "@remotion/google-fonts";
import InfiniteScrollWrapper from "../../../components/infinite-scroll-wrapper";
import NumberOfSlideSetter from "./number-of-slide-setter";
import { getStoredWorkspace } from "../../../api/workspace";

const assetsURL = import.meta.env.VITE_ASSETS_URL;
const per_page = 25;

function Saved() {
  const { state } = useLocation();
  const { postContent } = state || {};
  const { requestMaker } = useRequest();
  const { showNotification } = React.useContext(NotificationContext);
  const navigate = useNavigate();
  const fontOptions = getAvailableFonts();
  const [showNumberOfSlidesSetter, setShowNumberOfSlidesSetter] =
    useState(false);
  const [choosenTemplate, setChoosenTemplate] = useState(null);

  const pageFlowType =
    postContent != null ? "templateSelectionFlow" : "default";

  let pageContent = {
    default: {
      title: "Carousel Maker",
      description: "Design high-performing LinkedIn carousel posts in minutes.",
      action: "Click on any template to get started",
    },
    templateSelectionFlow: {
      title: "Choose a template",
      description: "Select one template to generate carousel for your content",
      action: "Click on any template",
    },
  };

  const [activeCarousel, setActiveCarousel] = React.useState(null);

  const [carousels, setCarousels] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);

  const fetchSavedCarousels = useCallback(() => {
    requestMaker(
      CarouselsRequestFactory.createGetCarouselsRequest(page, per_page)
    )
      .then(async (response) => {
        if (response.carousels.length === 0) {
          return;
        }
        const fontLoadingPromises = response.carousels.map(
          async (carousel, idx) => {
            const primaryFont = carousel.common_settings?.elements?.find(
              (element) => element.key === "primary_font"
            )?.value;

            const secondaryFont = carousel.common_settings?.elements?.find(
              (element) => element.key === "secondary_font"
            )?.value;

            const primaryFF = fontOptions.find(
              (f) => f.fontFamily === primaryFont
            );
            const secondaryFF = fontOptions.find(
              (f) => f.fontFamily === secondaryFont
            );
            await primaryFF?.load().then((f) => {
              f?.loadFont();
            });
            await secondaryFF?.load().then((f) => {
              f?.loadFont();
            });
          }
        );
        await Promise.all(fontLoadingPromises);
        setCarousels([...carousels, ...response.carousels]);
        setPage(page + 1);
        if (response.carousels.length < per_page) {
          setHasMore(false);
        }
      })
      .catch((error) => {
        console.error(error);
        showNotification(
          {
            title: "Something went wrong",
            content: "Please try again later",
          },
          "error"
        );
      });
  }, [requestMaker, page, carousels, fontOptions, showNotification]);

  useEffect(() => {
    fetchSavedCarousels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestMaker]);

  const duplicateCarousel = (carousel) => {
    let carouselCopy = JSON.parse(JSON.stringify(carousel));
    carouselCopy.id = null;
    requestMaker(
      CarouselsRequestFactory.createSaveCarouselRequest(
        carouselCopy.slides,
        carouselCopy.common_settings,
        "",
        carouselCopy.template_id
      )
    )
      .then((response) => {
        const currentWorkspaceId = getStoredWorkspace();
        navigate(`/ai/carousels/${response.carousel.id}?workspace_id=${currentWorkspaceId}`);
      })
      .catch((error) => {
        console.error(error);
        showNotification(
          {
            title: "Something went wrong",
            content: "Please try again later",
          },
          "error"
        );
      });
  };

  const deleteCarousel = (carousel) => {
    requestMaker(
      CarouselsRequestFactory.createDeleteCarouselRequest(carousel.id)
    )
      .then((response) => {
        showNotification(
          {
            title: "Carousel deleted",
            content: "Your carousel has been deleted successfully",
          },
          "success"
        );
        setCarousels(carousels.filter((c) => c.id !== carousel.id));
      })
      .catch((error) => {
        console.error(error);
        showNotification(
          {
            title: "Something went wrong",
            content: "Please try again later",
          },
          "error"
        );
      });
  };

  const createCarousel = (carousel, numberOfSlides = 5) => {
    const currentWorkspaceId = getStoredWorkspace();

    requestMaker(
      CarouselsRequestFactory.createSaveCarouselRequest(
        carousel.slides,
        carousel.common_settings,
        postContent,
        carousel.template_id
      )
    )
      .then((response) => {
        if (postContent) {
          navigate(`/ai/carousels/text-to-carousel/?workspace_id=${currentWorkspaceId}`, {
            state: {
              carousel: response.carousel,
              numberOfSlides: numberOfSlides,
            },
          });
        } else {
          if (carousel.id === "4") {
            navigate(
              `/ai/carousels/${response.carousel.id}?type=advanced&workspace_id=${currentWorkspaceId}`
            );
          } else {
            navigate(`/ai/carousels/${response.carousel.id}?workspace_id=${currentWorkspaceId}`);
          }
        }
      })
      .catch((error) => {
        console.error(error);
        showNotification(
          {
            title: "Error",
            content: "Something went wrong. Please try again later.",
          },
          "error"
        );
      });
  };

  return (
    <InfiniteScrollWrapper hasMore={hasMore} next={fetchSavedCarousels}>
      <div className="px-4 py-6 sm:px-6">
        <div className="space-y-2 sm:space-y-8">
          <div className="flex flex-col justify-between gap-4 sm:gap-6 sm:flex-row sm:items-center">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                {pageContent[pageFlowType].title}
              </h1>

              <p className="mt-1 text-sm font-normal text-gray-500">
                {pageContent[pageFlowType].description}
              </p>
            </div>
          </div>
          <div>
            <Nav
              hideTextToCarousel={
                postContent != null &&
                postContent !== undefined &&
                postContent !== ""
              }
              key={carousels.length}
            ></Nav>
            <div className="mt-10">
              <div className="grid grid-cols-1 gap-6 mt-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4">
                {carousels?.map((carousel) => {
                  return (
                    <div key={carousel.id} className="m-2 relative group">
                      <div
                        onClick={() => {
                          setChoosenTemplate(carousel);
                          if (postContent) {
                            setShowNumberOfSlidesSetter(true);
                          } else {
                            const currentWorkspaceId = getStoredWorkspace();
                            navigate(`/ai/carousels/${carousel.id}?workspace_id=${currentWorkspaceId}`);
                          }
                        }}
                        className="relative rounded-xl ring-1 ring-gray-200 shadow-xs cursor-pointer"
                      >
                        <div
                          style={{
                            zoom: 0.6,
                            pointerEvents: "none",
                          }}
                          className="flex justify-center items-center"
                        >
                          {carousel.slides?.map((slide, index) => {
                            return GetTemplateByID(
                              carousel.template_id
                            ).renderSlide(
                              slide,
                              index,
                              0,
                              carousel.slides.length,
                              carousel.common_settings,
                              null,
                              null,
                              null,
                              null
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-normal text-gray-600 mt-0.5">
                            Last saved on{" "}
                            <DateInUserTimezone date={carousel.updated_at} />
                          </p>
                        </div>
                        <div className="relative flex">
                          {!postContent && (
                            <button
                              onClick={() => {
                                activeCarousel === carousel.id
                                  ? setActiveCarousel(null)
                                  : setActiveCarousel(carousel.id);
                              }}
                              type="button"
                              className="inline-flex items-center justify-center p-2 text-gray-500 transition-all duration-150 rounded-full bg-gray-50 group hover:text-gray-700 hover:ring-gray-200 ring-1 ring-transparent"
                            >
                              <span className="sr-only">More Options</span>
                              <svg
                                aria-hidden="true"
                                className="w-5 h-5 text-gray-400 group-hover:text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                              </svg>
                            </button>
                          )}
                          {activeCarousel === carousel.id && (
                            <div
                              className="absolute right-0 z-50 w-40 mt-2 origin-top-right bg-white shadow-xl top-9 rounded-xl ring-1 ring-black ring-opacity-5 focus:outline-none"
                              role="menu"
                            >
                              <div className="py-1" role="none">
                                <Link
                                  to={() => {
                                    const currentWorkspaceId = getStoredWorkspace();
                                    return `/ai/carousels/${carousel.id}?workspace_id=${currentWorkspaceId}`;
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-150 group hover:text-gray-900 hover:bg-gray-50"
                                  role="menuitem"
                                  tabIndex={-1}
                                  id="menu-item-0"
                                >
                                  <svg
                                    aria-hidden="true"
                                    className="w-5 h-5 text-gray-500 group-hover:text-gray-600"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                                    <path
                                      fillRule="evenodd"
                                      d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Edit
                                </Link>
                                <button
                                  onClick={() => duplicateCarousel(carousel)}
                                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-150 group hover:text-gray-900 hover:bg-gray-50"
                                  role="menuitem"
                                  tabIndex={-1}
                                  id="menu-item-0"
                                >
                                  <svg
                                    aria-hidden="true"
                                    className="w-5 h-5 text-gray-500 group-hover:text-gray-600"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                                    <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                                  </svg>
                                  Duplicate
                                </button>
                                <button
                                  onClick={() => deleteCarousel(carousel)}
                                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-150 group hover:text-gray-900 hover:bg-gray-50"
                                  role="menuitem"
                                  tabIndex={-1}
                                  id="menu-item-0"
                                >
                                  <svg
                                    aria-hidden="true"
                                    className="w-5 h-5 text-gray-500 group-hover:text-gray-600"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {carousels.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 gap-y-8">
                  <div className="max-w-sm mx-auto text-center">
                    <img
                      className="w-auto h-48 mx-auto sm:h-64"
                      src={assetsURL + "empty-drafts.svg"}
                      alt=""
                    />
                    <h2 className="mt-6 text-xl font-semibold text-gray-900">
                      Nothing in saved yet
                    </h2>
                    <p className="mt-2 text-base font-normal text-gray-500">
                      Start creating something fabulous
                    </p>
                  </div>

                  <div className="w-12 h-px bg-gray-200"></div>

                  <div className="max-w-sm mx-auto text-center">
                    <p className="text-base font-medium text-gray-900">
                      Choose a template to get started
                    </p>
                    <div className="mt-4">
                      <Link
                        to={() => {
                          const currentWorkspaceId = getStoredWorkspace();
                          return `/ai/carousels?workspace_id=${currentWorkspaceId}`;
                        }}
                        title="Explore Templates"
                        className="inline-flex items-center justify-center gap-2 transition-all duration-150 rounded-full px-3 py-2.5 sm:px-4 sm:py-2.5 text-sm font-semibold ring-1 ring-inset ring-blue-200 bg-blue-50 text-blue-500"
                      >
                        <svg
                          aria-hidden="true"
                          className="w-5 h-5"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Explore Templates
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {showNumberOfSlidesSetter && (
          <NumberOfSlideSetter
            generateCarousel={(val) => {
              createCarousel(choosenTemplate, val);
            }}
          />
        )}
      </div>
    </InfiniteScrollWrapper>
  );
}

export default Saved;
